// src/pages/LessonPage.jsx
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import Header from '../components/Header';
import GrammarNotes from '../exercises/GrammarNotes';
import QuizExercise from '../exercises/QuizExercise';
import FillInTheBlankExercise from '../exercises/FillInTheBlankExercise';
import MatchingExercise from '../exercises/MatchingExercise';
import ListeningExercise from '../exercises/ListeningExercise';
import PronunciationExercise from '../exercises/PronunciationExercise';
import ErrorDetectionExercise from '../exercises/ErrorDetectionExercise';
import ReadingExercise from '../exercises/ReadingExercise';
import WritingExercise from '../exercises/WritingExercise';
import SpeakingExercise from '../exercises/SpeakingExercise';
import EngineMode from "../engines/EngineMode";
import './LessonPage.css';
import { ArrowLeft, ArrowRight, Home } from 'lucide-react';
import usePageTracker from "../hooks/usePageTracker.js";
import { useSubscriptionStatus, PaywallOverlay } from '../components/guards/SubscriptionGuard';

const exerciseMap = {
    grammar_notes: { Component: GrammarNotes, icon: '📝', name: 'Notas de Gramática' },
    quiz: { Component: QuizExercise, icon: '❓', name: 'Quiz' },
    fill_in_the_blank: { Component: FillInTheBlankExercise, icon: '✏️', name: 'Fill in the Blank' },
    matching: { Component: MatchingExercise, icon: '🔗', name: 'Matching' },
    listening: { Component: ListeningExercise, icon: '🎧', name: 'Listening' },
    pronunciation: { Component: PronunciationExercise, icon: '🗣️', name: 'Pronunciación' },
    error_detection: { Component: ErrorDetectionExercise, icon: '🔍', name: 'Detectar Errores' },
    reading: { Component: ReadingExercise, icon: '📖', name: 'Reading' },
    writing: { Component: WritingExercise, icon: '✍️', name: 'Writing' },
    conversation_spacy_prompt: { Component: SpeakingExercise, icon: '💬', name: 'Speaking Practice' }
};

const EXERCISE_ORDER = [
    'conversation_spacy_prompt',
    'grammar_notes',
    'quiz',
    'fill_in_the_blank',
    'matching',
    'listening',
    'pronunciation',
    'error_detection',
    'reading',
    'writing'
];

function LessonPage() {
    const [lesson, setLesson] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const { lesson_id } = useParams();
    const [activeExerciseIndex, setActiveExerciseIndex] = useState(0);
    const [completedExercises, setCompletedExercises] = useState([]);
    const [startTime, setStartTime] = useState(null);
    // ══════════════════════════════════════════════════════════════════════
    //  UN SOLO CAMINO
    //
    //  Aquí vivía el selector "¿Modo Clásico o Modo IA?". Se retiró: obligaba
    //  al alumno a decidir algo que no puede saber antes de entrar, y partía
    //  la lección en dos mundos entre los que había que brincar para tener
    //  gramática, ejercicios y Spacy.
    //
    //  Ahora se entra directo al motor. El contenido curado no desapareció:
    //  dejó de ser un MODO y pasó a ser la RESERVA. Y Spacy es un paso del
    //  camino, ya no un destino al que hay que irse.
    //
    //  Al temario curado ('classic') se llega por tres vías, y ninguna es
    //  "elige tu mundo":
    //    1. Automática — el backend sirve bloques curados dentro del propio
    //       camino con IA cuando se agotan los tokens (`curated_fallback.py`).
    //    2. Automática — si la lección no tiene `motor_type`, el motor no
    //       arranca y se sirve el temario con esta interfaz.
    //    3. A petición — "Practicar con el temario NSL", para el alumno que ya
    //       gastó los tokens de su plan y quiere seguir con los ejercicios
    //       completos del temario, que no cuestan nada.
    //
    //  En los tres casos el temario va SIN Spacy: vive en el camino con IA y
    //  no debe existir en dos sitios a la vez.
    // ══════════════════════════════════════════════════════════════════════
    const [modeSelected, setModeSelected] = useState('adventure'); // 'adventure' | 'classic'
    const { status } = useSubscriptionStatus();

    // ── Spacy como paso del camino ────────────────────────────────────────
    // El estado vive aquí porque aquí se monta SpeakingExercise. Se recuerda en
    // sessionStorage y NO en EngineProgress: esa tabla está indexada por bloque
    // del motor, y la conversación no es un bloque del motor — meterla ahí
    // desalinearía el progreso de todos los demás.
    const spacyKey = `lp_spacy_done_${lesson_id}`;
    const [showSpacy, setShowSpacy] = useState(false);
    const [spacyDone, setSpacyDone] = useState(false);
    // Contador, no booleano: cada pulsación de "Continuar" es una señal nueva
    // para el motor. Con un booleano, continuar dos veces seguidas no se
    // distinguiría de no haber continuado.
    const [resumeSignal, setResumeSignal] = useState(0);

    useEffect(() => {
        setShowSpacy(false);
        try { setSpacyDone(sessionStorage.getItem(spacyKey) === '1'); }
        catch { setSpacyDone(false); }
    }, [spacyKey]);

    const markSpacyDone = () => {
        setSpacyDone(true);
        try { sessionStorage.setItem(spacyKey, '1'); } catch { /* almacenamiento bloqueado */ }
    };

    const navigate = useNavigate();

    usePageTracker(
        "lesson",
        lesson?.title ? `Lección: ${lesson.title}` : "Lección Interactiva",
        null,
        "studying"
    );

    /* ── TEMA ──────────────────────────────────────────────────────────────
       Aqui se forzaba `data-theme="dark"` en <html> y <body> siempre que el
       alumno no estuviera en el temario. Eso hacia imposible el modo claro:
       daba igual lo que el usuario tuviera elegido, la leccion se pintaba
       oscura y al salir se restauraba su preferencia.

       Se retiro. EngineMode ya define su paleta para los DOS temas en
       EngineMode.css (tokens `--em-*`), asi que respeta la eleccion del
       usuario como el resto de la aplicacion. ────────────────────────────── */


    // Fuerza el header visible al entrar en modo Spacy
    useEffect(() => {
        window.dispatchEvent(new Event('header:forceshow'));
    }, [activeExerciseIndex]);

    useEffect(() => {
        setStartTime(Date.now());
    }, [activeExerciseIndex]);

    const sendProgressToServer = async (exerciseId, durationInSeconds) => {
        const token = localStorage.getItem('token');
        if (!token) return;
        try {
            await fetch('/api/progress/complete-exercise', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    exercise_id: exerciseId,
                    duration_seconds: durationInSeconds
                })
            });
        } catch (error) {
            console.error("Hubo un problema al enviar el progreso:", error);
        }
    };

    const handleCorrectAnswer = (exerciseId) => {
        if (!completedExercises.includes(exerciseId)) {
            const endTime = Date.now();
            const durationInSeconds = startTime ? Math.round((endTime - startTime) / 1000) : 0;
            setCompletedExercises(prevCompleted => [...prevCompleted, exerciseId]);
            sendProgressToServer(exerciseId, durationInSeconds);
        }
    };

    useEffect(() => {
        const token = localStorage.getItem('token');
        setIsLoading(true);
        setLesson(null);
        setActiveExerciseIndex(0);
        setModeSelected('adventure');   // cada lección abre en el camino único

        const fetchLessonData = fetch(`/api/course/lesson/${lesson_id}`, {
            headers: { 'Authorization': `Bearer ${token}` }
        });
        const fetchCompletedData = fetch('/api/progress/completed-exercises', {
            headers: { 'Authorization': `Bearer ${token}` }
        });

        Promise.all([fetchLessonData, fetchCompletedData])
            .then(async ([lessonRes, completedRes]) => {
                if (!lessonRes.ok) throw new Error("Error al obtener los datos de la lección.");
                const lessonData = await lessonRes.json();
                const completedIds = completedRes.ok ? await completedRes.json() : [];

                if (lessonData && lessonData.exercises) {
                    lessonData.exercises.sort((a, b) => {
                        const indexA = EXERCISE_ORDER.indexOf(a.exercise_type);
                        const indexB = EXERCISE_ORDER.indexOf(b.exercise_type);
                        if (indexA === -1) return 1;
                        if (indexB === -1) return -1;
                        return indexA - indexB;
                    });
                }

                setLesson(lessonData);
                setCompletedExercises(completedIds);
                setStartTime(Date.now());
            })
            .catch(error => {
                console.error("Error al cargar la lección:", error);
                setLesson(null);
            })
            .finally(() => {
                setIsLoading(false);
            });
    }, [lesson_id]);

    const handleNavigate = (newLessonId) => {
        if (newLessonId) navigate(`/lesson/${newLessonId}`);
    };

    // ── PANTALLA DE CARGA ──────────────────────────────────────────────────
    if (isLoading) return <div>Cargando lección...</div>;

    // ── LECCIÓN NO ENCONTRADA ──────────────────────────────────────────────
    if (!lesson) return <div>Lección no encontrada. (Verifique la consola para más detalles)</div>;

    // ══════════════════════════════════════════════════════════════════════
    //  Aqui vivia la pantalla del selector de modos (dos tarjetas: Modo
    //  Clasico / Modo IA NSL). Se retiro entera. El alumno ya no elige un
    //  mundo: entra a la leccion y avanza en linea recta.
    // ══════════════════════════════════════════════════════════════════════

    // ══════════════════════════════════════════════════════════════════════
    //  MODO IA NSL → EngineMode
    // ══════════════════════════════════════════════════════════════════════
    if (modeSelected === 'adventure') {
        // ── SPACY, MUDADO SIN TOCARLO ──────────────────────────────────────
        //
        // El ejercicio se arma EXACTAMENTE igual que lo armaba el Modo Clásico
        // más abajo en este mismo archivo: mismas claves, mismo `lesson_id`,
        // mismo `title` inyectado. Y se renderiza el MISMO componente
        // `SpeakingExercise`, con las mismas tres props.
        //
        // Se monta aquí y no dentro de EngineMode a propósito: el contenedor
        // `.engine-mode` impone `overflow-x: hidden` y
        // `button { white-space: nowrap; overflow: hidden }` a todo lo que
        // cuelga de él, y eso le rompería el modo cinema y los botones. Aquí
        // recibe exactamente el mismo entorno que tenía en el Modo Clásico.
        const spacyRow = (lesson.exercises || [])
            .find(ex => ex.exercise_type === 'conversation_spacy_prompt');
        // ── EL NIVEL, QUE ANTES NO LLEGABA ──────────────────────────────────
        // El wizard leía `user_level` del `data` del ejercicio, y ese JSON no lo
        // trae: sólo tiene título, objetivos y tarjetas. Así que `userLevel`
        // llegaba `undefined` y caía en su valor por defecto — 'A1' — para
        // TODOS los alumnos. Por eso un B1 veía la pantalla de A1 y el motor de
        // voz recibía el nivel equivocado.
        //
        // El nivel real viene de `motor_metadata.level` de la lección.
        // `level_code` sale de la tabla `levels`; `motor_metadata.level` es sólo
        // el respaldo, igual que hace `motor-info` en el backend.
        const nivelLeccion = lesson.level_code || lesson.motor_metadata?.level || null;
        const spacyExercise = spacyRow ? {
            ...spacyRow,
            lesson_id: lesson.id,
            data: { ...spacyRow.data, title: lesson.title, user_level: nivelLeccion },
        } : null;

        // ── POR QUÉ SE PINTAN LOS DOS A LA VEZ ─────────────────────────────
        //
        // Antes esto era un `return` aparte: al mostrar Spacy, EngineMode
        // desaparecía del árbol y React lo DESMONTABA. Al volver, renacía en
        // su estado inicial — la pantalla de configuración — y el alumno tenía
        // que volver a elegir contexto y nivel después de cada conversación.
        //
        // Ahora EngineMode se queda montado y sólo se OCULTA. Vuelve al mapa
        // exactamente donde estaba, con su progreso y su configuración.
        const enSpacy = showSpacy && !!spacyExercise;
        const sinTokens = status === 'tokens';

        return (
            <>
                {enSpacy && (
                <>
                    <Header />
                    <div className="lp-spacy-step-bar">
                        <button
                            className="lp-spacy-back"
                            onClick={() => setShowSpacy(false)}
                        >
                            ← Volver al mapa de la lección
                        </button>
                        <span className="lp-spacy-step-label">
                            {spacyDone ? '✅ Conversación completada' : '🤖 Conversación con Spacy'}
                        </span>
                        <button
                            className="lp-spacy-next"
                            onClick={() => {
                                markSpacyDone();
                                setShowSpacy(false);
                                setResumeSignal(n => n + 1);   // entra al siguiente bloque
                            }}
                        >
                            Continuar →
                        </button>
                    </div>

                    {sinTokens ? (
                        <div style={{ position: 'relative', width: '100%', minHeight: '500px', borderRadius: '12px', overflow: 'hidden' }}>
                            <PaywallOverlay blockStatus="tokens" isEmbedded={true} />
                        </div>
                    ) : (
                        <SpeakingExercise
                            exerciseId={spacyExercise.id}
                            exerciseData={spacyExercise}
                            onCorrectAnswer={markSpacyDone}
                        />
                    )}
                </>
                )}

                {/* El motor NO se desmonta al abrir Spacy: sólo se oculta.
                    `display: contents` lo deja como si este div no existiera,
                    así que el layout es idéntico al de antes.

                    Aquí también había un muro: si `status === 'tokens'` se
                    pintaba el paywall y el alumno no entraba. Ya no — el
                    backend degrada solo y sirve el temario curado. Los
                    problemas de PAGO sí siguen bloqueando, con 402/403. */}
                <div style={{ display: enSpacy ? 'none' : 'contents' }}>
                    <EngineMode
                        lessonId={parseInt(lesson_id)}
                        onExit={() => navigate('/home')}
                        hasSpacy={!!spacyExercise}
                        spacyDone={spacyDone}
                        onOpenSpacy={() => setShowSpacy(true)}
                        resumeSignal={resumeSignal}
                        // Puerta al temario curado. No es "el otro modo": es la
                        // reserva para quien agotó los tokens de su plan.
                        onGoClassic={() => setModeSelected('classic')}
                        // Red de seguridad automática: si la lección no tiene
                        // motor, se sirve el temario. El alumno no elige nada.
                        onMotorUnavailable={() => setModeSelected('classic')}
                    />
                </div>
            </>
        );
    }

    // ══════════════════════════════════════════════════════════════════════
    //  TEMARIO CURADO (antes "Modo Clásico")
    //
    //  El contenido queda intacto — mismos ejercicios, mismos componentes,
    //  misma UI — con UNA excepción: Spacy ya no está aquí. Se mudó al camino
    //  con IA y no debe existir en dos sitios a la vez; duplicarlo haría que
    //  el alumno lo encontrara dos veces y no supiera cuál "cuenta".
    //
    //  Se llega por dos caminos, ninguno de ellos una elección de "modo":
    //    · El alumno sin tokens que pulsa "Practicar con el temario NSL".
    //    · Automáticamente, si la lección no tiene motor asignado.
    // ══════════════════════════════════════════════════════════════════════
    const curatedExercises = (lesson.exercises || [])
        .filter(ex => ex.exercise_type !== 'conversation_spacy_prompt');

    if (curatedExercises.length === 0) {
        return (
            <>
                <Header />
                <div className="lp-curated-empty">
                    <p>Esta lección no tiene ejercicios del temario.</p>
                    <button className="lp-spacy-next" onClick={() => setModeSelected('adventure')}>
                        ← Volver a la clase
                    </button>
                </div>
            </>
        );
    }

    // El índice puede venir de una lista más larga (la que aún incluía a Spacy).
    const safeIndex = Math.min(activeExerciseIndex, curatedExercises.length - 1);
    const activeExercise = curatedExercises[safeIndex];
    const ActiveComponentInfo = exerciseMap[activeExercise.exercise_type];

    return (
        <>
            {/* ══════════════════════════════════════════════════════════════════
                El modal "Cambiar a Modo IA NSL" vivia aqui. Se retiro junto
                con el selector: ya no hay dos modos entre los que cambiar.
                ══════════════════════════════════════════════════════════════════ */}
            <div className="lp-animated-bg">
                <div className="lp-bg-stars"></div>
            </div>
            <div className="lp-page-wrapper">
                <div>
                    <Header />
                    <div className="lp-page-header-wrapper">
                        <header className="lp-lesson-header">
                            <h1 className="lp-lesson-title">{lesson.title}</h1>
                            <p className="lp-lesson-subtitle">{lesson.description || 'Completa los ejercicios para avanzar.'}</p>
                            {/* Sin esto el alumno queda atrapado en el temario hasta
                                recargar la pagina: entro con un clic, tiene que poder
                                salir con otro. */}
                            <button
                                className="lp-back-to-class"
                                onClick={() => setModeSelected('adventure')}
                            >
                                ← Volver a la clase con IA
                            </button>
                        </header>
                    </div>

                    <div className="lp-mobile-nav">
                        <div className="lp-mobile-nav-grid">
                            {curatedExercises.map((exercise, index) => (
                                <button
                                    key={`mobile-${exercise.id}`}
                                    className={`lp-mobile-nav-item ${index === safeIndex ? 'active' : ''} ${completedExercises.includes(exercise.id) ? 'completed' : ''}`}
                                    onClick={() => setActiveExerciseIndex(index)}
                                >
                                    {exerciseMap[exercise.exercise_type]?.icon || '⭐'}
                                </button>
                            ))}
                            <div className="lp-mobile-nav-label-wrapper">
                                <p className="lp-mobile-nav-label">
                                    {exerciseMap[activeExercise.exercise_type]?.name || 'Ejercicio'}
                                </p>
                            </div>
                        </div>
                    </div>

                    <div className="lp-lesson-page-container">
                        <aside className="lp-sidebar">
                            <ul className="lp-module-list">
                                {curatedExercises.map((exercise, index) => (
                                    <React.Fragment key={exercise.id}>
                                        <li
                                            className={`lp-module-item ${index === safeIndex ? 'active' : ''} ${completedExercises.includes(exercise.id) ? 'completed' : ''}`}
                                            onClick={() => setActiveExerciseIndex(index)}
                                        >
                                            <span className="lp-module-icon">{exerciseMap[exercise.exercise_type]?.icon || '⭐'}</span>
                                            <div className="lp-module-info">
                                                <div className="lp-module-title">{exerciseMap[exercise.exercise_type]?.name || 'Ejercicio'}</div>
                                                <div className="lp-module-status">{completedExercises.includes(exercise.id) ? 'Completado' : 'Pendiente'}</div>
                                            </div>
                                        </li>
                                        {/* Aquí había un botón "Ejercicios con IA" que abría el
                                            modal para saltar al otro mundo. Se retiró con el
                                            selector: esta vista ya sólo aparece como red de
                                            seguridad cuando la lección no tiene motor, y
                                            ofrecer un salto a un motor que no existe sería
                                            mandar al alumno a una pantalla en blanco. */}
                                    </React.Fragment>
                                ))}
                            </ul>
                        </aside>
                        <main className="lp-main-content">
                            {/* Writing es el UNICO ejercicio del temario que llama a
                                la IA (/api/writing/feedback), asi que es el unico que
                                puede quedarse sin combustible. El resto es local y
                                funciona con cero tokens — que es justo el motivo de
                                que este camino exista. */}
                            {status === 'tokens' && activeExercise.exercise_type === 'writing' ? (
                                <div style={{ position: 'relative', width: '100%', height: '100%', minHeight: '500px', borderRadius: '12px', overflow: 'hidden' }}>
                                    <PaywallOverlay blockStatus="tokens" isEmbedded={true} />
                                </div>
                            ) : ActiveComponentInfo ? (
                                // La rama especial para `conversation_spacy_prompt`
                                // desapareció: Spacy ya no forma parte de esta lista.
                                // Se monta arriba, en el camino con IA, con esas
                                // mismas props.
                                <ActiveComponentInfo.Component
                                    exerciseData={activeExercise.data}
                                    onCorrectAnswer={() => handleCorrectAnswer(activeExercise.id)}
                                />
                            ) : (
                                <div>Error: No se encontró un componente para el tipo de ejercicio "{activeExercise.exercise_type}"</div>
                            )}
                        </main>
                    </div>
                </div>

                <div className="lesson-navigation-wrapper">
                    <div className="lesson-navigation">
                        <button
                            className="nav-button prev-button"
                            onClick={() => handleNavigate(lesson.previous_lesson_id)}
                            disabled={!lesson.previous_lesson_id}
                        >
                            <ArrowLeft size={20} />
                            Back
                        </button>
                        <Link to="/home" className="nav-button course-button">
                            <Home size={20} />
                            Course
                        </Link>
                        <button
                            className="nav-button next-button"
                            onClick={() => handleNavigate(lesson.next_lesson_id)}
                            disabled={!lesson.next_lesson_id}
                        >
                            Next
                            <ArrowRight size={20} />
                        </button>
                    </div>
                </div>
            </div>
        </>
    );
}

export default LessonPage;